import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Budget, BudgetSummary, ResumeMaterial, SubBudgetTotals} from '../../interfaces';
import {DataService} from '../../services/data.service';
import {AuthService} from '../../services/auth.service';
import {ActivitiesValueModel} from '../models/activities-value';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {MatDialog} from '@angular/material/dialog';
import {MatSort, MatSortHeader} from '@angular/material/sort';
import {LinkListDialogComponent} from '../../shared/link-list-dialog/link-list-dialog.component';
import {filter, takeUntil} from 'rxjs/operators';
import {combineLatest, Subject} from 'rxjs';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {OrderService} from '../../services/orders.service';
import {CommonModule} from '@angular/common';
import {MatCardModule} from '@angular/material/card';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatMenuItem} from '@angular/material/menu';
import {MatTooltip} from '@angular/material/tooltip';
import {sortActivities} from '../../util';



@Component({
    selector: 'app-sub-budget-resume',
    templateUrl: './sub-budget-resume.component.html',
    styleUrls: ['./sub-budget-resume.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        MatCardModule,
        MatToolbarModule,
        MatButtonModule,
        MatIconModule,
        MatExpansionModule,
        MatTableModule,
        MatSort,
        MatSortHeader,
        MatMenuItem,
        MatTooltip
    ],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({height: '0px', minHeight: '0', display: 'none'})),
            state('expanded', style({height: '*'})),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
        ])
    ]
})
export class SubBudgetResumeComponent implements OnInit, OnDestroy {

    @Input() public budget: Budget;
    @Input() public budgetTotal: number;
    @Input() public tabSelector: number;
    @Input() public subBudgets: Map<String, String>;
    @ViewChild('activitiesSort') activitiesSort: MatSort;
    @ViewChild('materialsSort') materialsSort: MatSort;
    public subBudgetTotals = new SubBudgetTotals();
    public categoryMap = new Map<string, string>();
    public avm: any;
    public matDisplayedColumns = [];
    public matDisplayedColumnsSubItems = [];
    public displayedColumns = [];
    public disableGenerateSheet = true;
    public expandedElement: any;
    public groupedMaterials: any[];
    public activitiesDataSource: MatTableDataSource<any> = new MatTableDataSource<any>([]);
    public materialsDataSource: MatTableDataSource<ResumeMaterial> = new MatTableDataSource<ResumeMaterial>([]);
    private activityValMap: ActivitiesValueModel;
    private unsubscribe$ = new Subject();

    constructor(
        private dataService: DataService,
        public dialog: MatDialog,
        private auth: AuthService,
        private orderService: OrderService) {
    }

    private _resume: BudgetSummary;

    get resume() {
        return this._resume;
    }

    private _budgetId: string;
    @Input() set budgetId(_budgetId: string) {
        if (!_budgetId) return;
        this._budgetId = _budgetId;

        combineLatest([
            this.dataService.getSubBudgetResume(_budgetId),
            this.dataService.getItemCategories()
        ])
            .pipe(
                takeUntil(this.unsubscribe$),
                filter((summary) => summary !== undefined))
            .subscribe(([resume, categories]) => {
                this._resume = resume as BudgetSummary;
                if (categories) {
                    this.categoryMap.clear();
                    categories.forEach((c) => {
                        this.categoryMap.set(c.name, c.description);
                    });
                }

                this.disableGenerateSheet = false;
                this.activityValMap = new ActivitiesValueModel(this._resume.activities);
                this.avm = this.activityValMap.activityValueMap;
                this.subBudgetTotals.activities = this._resume.activities.filter((a) => a.cost && a.hours)
                    .map((a) => a.cost * a.hours).reduce((v1, v2) => v1 + v2, 0);
                this.subBudgetTotals.materials = this._resume.materials.filter((m) => m.costEach && m.quantity)
                    .map((m) => m.costEach * m.quantity).reduce((v1, v2) => v1 + v2, 0);
                this.groupedMaterials = this.groupMaterials(this._resume.materials);

                this.activitiesDataSource.data = [...(this._resume.activities ?? [])].sort((a, b) => sortActivities(a, b, this.categoryMap));
                this.activitiesDataSource.sort = this.activitiesSort;
                this.activitiesDataSource.sortingDataAccessor = (item, property) => {
                    switch (property) {
                        case 'activity':
                        case 'category':
                            return item[property]?.toLocaleLowerCase();
                        case 'cost':
                        case 'hours':
                        case 'finalHours':
                        case 'importoCalc':
                        case 'finalAmount':
                        case 'weight':
                        case 'difference':
                            return +item[property];
                        default:
                            return item[property];
                    }
                };
                this.materialsDataSource.data = this.groupedMaterials ?? [];
                this.materialsDataSource.sort = this.materialsSort;
                this.materialsDataSource.sortingDataAccessor = (item, property) => {
                    switch (property) {
                        case 'description':
                            return item[property]?.toLocaleLowerCase();
                        case 'category':
                            return this.categoryMap.get(item[property]);
                        case 'unit':
                            return item[property]?.toLocaleLowerCase();
                        case 'quantity':
                        case 'finalQuantity':
                        case 'costEach':
                        case 'finalAmount':
                            return +item[property];
                        case 'weight':
                            return 100 * item.amount / this.budgetTotal;
                        case 'difference':
                            return item.amount - item.finalAmount;
                        case 'amount':
                            return item.quantity * item.costEach;
                        default:
                            return item[property];
                    }
                };

                return resume;
            });
    }

    ngOnInit() {

        this.auth.userHasPermissions(['app.budget.figures.read', 'app.budget.final.read', 'app.budget.write', 'app.iproject.write'])
            .subscribe((userPerm: any[]) => {
                if (userPerm[0] && userPerm[1]) {
                    // Can see figures and finals
                    this.displayedColumns = ['name', 'category', 'cost', 'hours', 'finalHours', 'importoCalc', 'finalAmount', 'weight', 'difference'];
                    this.matDisplayedColumns =
                        ['description', 'category', 'unit', 'quantity', 'finalQuantity',
                            'costEach', 'amount', 'finalAmount', 'weight', 'difference'];
                    this.matDisplayedColumnsSubItems = ['description', 'category', 'unit', 'finalQuantity',
                        'costEach', 'finalAmount'];
                } else if (userPerm[0]) {
                    // Can only see figures
                    this.displayedColumns = ['name', 'category', 'cost', 'hours', 'importoCalc', 'weight'];
                    this.matDisplayedColumns = ['description', 'category', 'unit', 'quantity',
                        'costEach', 'amount', 'weight'];
                    this.matDisplayedColumnsSubItems = ['description', 'category', 'unit'];
                }
            });
    }

    public ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    /**
     * Groups materials referencing materials in the "sons" array, for graphical purposes
     * @param materials
     */
    public groupMaterials(materials: Array<any>) {
        const fathersIds = materials.map(m => {
            if (m.isOnlyFinal && m.referencedMaterialId) {
                return m.referencedMaterialId;
            }
            return null;
        }).filter(m => m !== null);

        return materials.map((m: any) => {
            const isFather = fathersIds.find(id => id === m.id);
            m.sons = [];
            if (isFather) {
                m.sons = materials
                    .map(mat => mat.referencedMaterialId === m.id ? mat : null)
                    .filter(ma => ma !== null);
                m.finalAmount = m.finalAmount + m.sons.reduce((a: number, b: any) => a + b.finalAmount, 0);
            }
            return m;
        }).filter(m => (!m.referencedMaterialId));
    }

    public async createResumeSheet() {
        this.disableGenerateSheet = true;

        this.dataService.setReportSubBudget(this._budgetId).subscribe(res => {
            this.disableGenerateSheet = false;
            console.log(res);
        }, err => {
            console.error(err);
            this.disableGenerateSheet = false;
        });
    }

    public setExpandedNode(element: any) {
        if (this.expandedElement !== element) {
            this.expandedElement = element;
        } else {
            this.expandedElement = undefined;
        }
    }

    async openOrderLinkList(row: any) {
        if (!row.processedOrders) {
            return;
        }
        row.processedOrders = [...new Set((row.processedOrders as string[]))];

        const orders = [];
        for (const orderId of row.processedOrders) {
            const order = await this.orderService.getSingleOrder(orderId);
            orders.push({...order.data(), id: order.id});
        }

        const links = orders.map(o => {
            return {
                descr: `${o.orderNr} - ${o.vendor ? o.vendor.name : ''}`, link: `#/order/view/${o.id}`
            };
        });
        const title = 'Linked orders';


        this.dialog.open(LinkListDialogComponent, {
            width: '500px',
            data: {links: links, title: title}
        });
    }
}